/**
 * Danish Yahtzee rules engine.
 *
 * A direct implementation of the algorithm in docs/yahtzee/PRD.md section 4.3.
 * The PRD's central design principle (section 4.1) is that a game is an
 * append-only list of turns and **nothing derived is ever stored** -- totals,
 * strike counters, streaks, standings, the round number and completion are all
 * recomputed by replaying the log.
 *
 * That is what makes undo a tail removal rather than an inverse operation, and
 * what lets a rules change apply retroactively without migrating data. Resist
 * caching anything from here into the database.
 *
 * Two orderings inside the loop are load-bearing and easy to get backwards:
 *
 *   1. The third-strike penalty is applied **before** the target check of that
 *      same turn, so a penalty can in principle move a player back below the
 *      target on the very turn they would have crossed.
 *   2. The target check considers **only the player who just acted**. Someone
 *      else being above the target does not trigger the final round.
 */

export const RULES = {
  /** Reaching this ends the game after the round is played out. */
  target: 10_000,
  /** A turn may not be banked below this (PRD 2.3). */
  minBank: 350,
  /** Every banked score is a multiple of this (PRD 2.1). */
  granularity: 50,
  /** Consecutive strikes that trigger the penalty (PRD 2.4). */
  strikeLimit: 3,
  /** Deducted when the strike limit is reached; the counter then resets. */
  penalty: 1_000,
} as const;

export type PlayerId = string;

export interface Turn {
  playerId: PlayerId;
  type: "bank" | "strike";
  /** 0 for a strike; >= minBank and a multiple of granularity for a bank. */
  points: number;
}

export interface PlayerState {
  playerId: PlayerId;
  total: number;
  /** Consecutive strikes since the last bank or penalty reset. */
  strikes: number;
  /** Consecutive banked turns, for the streak badge (PRD 7). */
  streak: number;
  /** Longest streak reached at any point, for statistics. */
  bestStreak: number;
  /** Highest single banked turn. */
  best: number;
  /** How many times this player has taken the -1000. */
  penalties: number;
  banked: number;
  /** Points banked, before any strike penalty. The denominator for "Konstanz":
   *  total nets out the -1000s, which unfairly drags down a steady banker who
   *  merely also struck a few times. */
  bankedPoints: number;
  struck: number;
  /** Turns taken, banked plus struck. */
  turns: number;
}

export interface GameState {
  players: PlayerState[];
  /** Sorted by total descending; ties keep turn order. */
  standings: PlayerState[];
  /** Whose turn it is, or null once the game is finished. */
  current: PlayerId | null;
  /** 1-based. */
  round: number;
  finished: boolean;
  winner: PlayerId | null;
  /** Index of the turn on which someone first reached the target. */
  crossedAt: number | null;
  /** True once someone has crossed and the round is being played out. */
  isFinalRound: boolean;
  /** Turns still owed before the game ends, or null if nobody has crossed. */
  turnsRemaining: number | null;
}

export interface BankValidity {
  ok: boolean;
  /** Set when the value is below the minimum. */
  belowMinimum?: number;
  /** Set when the value is not a multiple of the granularity. */
  notMultiple?: boolean;
}

/**
 * PRD 4.4: the only two hard constraints. Section 3.3 is explicit that nothing
 * else may be validated -- house variations exist and the scorer is trusted, so
 * an unusual but legal total must not be second-guessed.
 */
export function validateBank(points: number): BankValidity {
  if (!Number.isFinite(points) || !Number.isInteger(points)) return { ok: false, notMultiple: true };
  const notMultiple = points % RULES.granularity !== 0;
  const below = points < RULES.minBank;
  if (!notMultiple && !below) return { ok: true };
  return {
    ok: false,
    ...(below ? { belowMinimum: RULES.minBank - points } : {}),
    ...(notMultiple ? { notMultiple: true } : {}),
  };
}

function emptyPlayer(playerId: PlayerId): PlayerState {
  return {
    playerId,
    total: 0,
    strikes: 0,
    streak: 0,
    bestStreak: 0,
    best: 0,
    penalties: 0,
    banked: 0,
    bankedPoints: 0,
    struck: 0,
    turns: 0,
  };
}

/**
 * Replay a turn log into the full derived state.
 *
 * `order` is fixed for the game (PRD 2.8) and the current player is derived by
 * modulo, which is why mid-game reordering is impossible by design (PRD 11.2).
 */
export function replay(order: PlayerId[], turns: Turn[]): GameState {
  const n = order.length;
  const byId = new Map<PlayerId, PlayerState>(order.map((id) => [id, emptyPlayer(id)]));

  if (n === 0) {
    return {
      players: [],
      standings: [],
      current: null,
      round: 1,
      finished: false,
      winner: null,
      crossedAt: null,
      isFinalRound: false,
      turnsRemaining: null,
    };
  }

  let crossedAt: number | null = null;

  for (const [index, turn] of turns.entries()) {
    // A turn for someone outside the order cannot be replayed meaningfully.
    // Skipping keeps a corrupt log readable rather than throwing at the table.
    const p = byId.get(turn.playerId);
    if (!p) continue;

    p.turns += 1;

    if (turn.type === "bank") {
      p.total += turn.points;
      p.strikes = 0;
      p.streak += 1;
      p.bestStreak = Math.max(p.bestStreak, p.streak);
      p.best = Math.max(p.best, turn.points);
      p.banked += 1;
      p.bankedPoints += turn.points;
    } else {
      p.strikes += 1;
      p.streak = 0;
      p.struck += 1;
      // PRD 2.4: the counter resets, so a fourth consecutive strike costs
      // nothing -- the penalty is per three, not per strike beyond two.
      if (p.strikes >= RULES.strikeLimit) {
        p.total -= RULES.penalty;
        p.strikes = 0;
        p.penalties += 1;
      }
    }

    // Deliberately after the penalty, and only for the player who just acted.
    if (crossedAt === null && p.total >= RULES.target) crossedAt = index;
  }

  // PRD 4.3: the round containing the crossing turn is played out in full, so
  // the last turn of the game is the final slot of that round.
  const lastIdx = crossedAt === null ? null : (Math.floor(crossedAt / n) + 1) * n - 1;
  const finished = lastIdx !== null && turns.length >= lastIdx + 1;

  const players = order.map((id) => byId.get(id)!);
  const standings = [...players].sort((a, b) => b.total - a.total);

  return {
    players,
    standings,
    current: finished ? null : order[turns.length % n]!,
    round: Math.floor(turns.length / n) + 1,
    finished,
    // PRD 2.7: the winner is the highest total, not whoever crossed first.
    winner: finished ? (standings[0]?.playerId ?? null) : null,
    crossedAt,
    isFinalRound: crossedAt !== null && !finished,
    turnsRemaining: lastIdx === null ? null : Math.max(0, lastIdx + 1 - turns.length),
  };
}

/** Undo is a tail removal, per PRD 4.1. */
export function undo(turns: Turn[]): Turn[] {
  return turns.slice(0, -1);
}

/**
 * Would this turn trigger the penalty? The live screen announces it (FR-3.7),
 * and asking beforehand avoids diffing two replays to find out.
 */
export function strikeWouldPenalise(state: GameState, playerId: PlayerId): boolean {
  const p = state.players.find((x) => x.playerId === playerId);
  return p !== undefined && p.strikes + 1 >= RULES.strikeLimit;
}
